import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { catchError, finalize, map, Observable, of, switchMap, take } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { ContractApiService } from '@portal/signing/data-access';
import { ROUTE } from '@portal/data-access';
import { VerificationVerifyingModel } from '../model';
import { PortalVerificationStore } from '../store';
import { VerificationApiService } from '../api';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent, ErrorDialogData } from '@portal/dialog';
import { LoadingService } from '@portal/loading/data-access';

@Injectable({ providedIn: 'root' })
export class SendingOtpGuard implements CanActivate {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(PortalVerificationStore);
  private readonly contractApi = inject(ContractApiService);
  private readonly verificationApi = inject(VerificationApiService);
  private readonly verificationUrlTree = this.router.createUrlTree([ROUTE.VERIFICATION.MAIN]);
  private readonly loadingService = inject(LoadingService);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const { code: appId, last4NRIC } = route.queryParams;

    if (appId && last4NRIC) return this.verifyingApplicant({ appId, last4NRIC });

    return this.store.sendingOtpState$.pipe(
      take(1),
      switchMap(state => {
        if (!state || !state.data || state.state !== 'sending-otp') {
          return of(this.verificationUrlTree);
        }

        return this.verifyingApplicant({ appId: state.data.appId, last4NRIC: state.data.last4NRIC });
      }),
    );
  }

  private verifyingApplicant(args: { appId: string; last4NRIC: string }) {
    this.loadingService.show();

    return this.contractApi.authenticate({ refCode: args.appId, authId: args.last4NRIC }).pipe(
      map(response => {
        this.store.setSid(response?.sid);
        return true;
      }), // allow navigation on sending otp page
      catchError(err => {
        const { errorCode, error } = err['error'] || {};

        if (errorCode === 'CTM022') {
          return this.sendOtpToApplicant(args);
        }

        // Handle invalid Application ID
        if (error?.['refCode']) {
          this.verificationFailedDialog();
          return of(false);
        }

        return of(true);
      }),
      finalize(() => this.loadingService.hide()),
    );
  }

  private sendOtpToApplicant(args: { appId: string; last4NRIC: string }): Observable<boolean | UrlTree> {
    const dto = VerificationVerifyingModel.fromJson(args);
    this.loadingService.show();

    return this.verificationApi.sendingOtp(dto).pipe(
      map(() => true), // allow navigation on sending otp page
      catchError(() => {
        this.verificationFailedDialog();
        this.store.setVerifying(dto);
        return of(false);
      }),
      finalize(() => this.loadingService.hide()),
    );
  }

  private verificationFailedDialog(): void {
    this.dialog.open<ErrorDialogComponent, ErrorDialogData, boolean>(ErrorDialogComponent, {
      data: { dialogContent: 'verificationFailed' },
      panelClass: '!max-w-md',
    });
  }
}
